/*
  PLP: come back to the card you left from.

  Infinite scroll pushes ?page=N as pages load, so Back re-renders only page N
  and the saved scroll offset no longer fits - shoppers landed near the top
  (client, 19 Sep 2026). On a card click we remember the pages on screen, the
  card and where it sat in the viewport; on Back we fetch the missing earlier
  pages through the section renderer, prepend them, and scroll the same card
  back under the same spot. Cards carry ref="cards[]", so <results-list> picks
  the prepended ones up through its ref observer and keeps paginating from the
  right page.
*/
import { getScrollContainer } from '@theme/scroll-container';

const KEY = 'it-plp-restore';
const MAX_AGE = 60 * 60 * 1000;
const list = document.querySelector('results-list[section-id]');

function listKey(href) {
  const url = new URL(href, window.location.href);
  url.searchParams.delete('page');
  url.searchParams.delete('section_id');
  const params = [...url.searchParams].sort(([a], [b]) => a.localeCompare(b));
  return url.pathname + '?' + params.map(([k, v]) => `${k}=${v}`).join('&');
}

function cards() {
  return [...list.querySelectorAll('[ref="grid"] > [ref="cards[]"]')];
}

function save(card) {
  const pages = cards().map((c) => Number(c.dataset.page)).filter(Number.isFinite);
  if (!pages.length) return;
  try {
    sessionStorage.setItem(KEY, JSON.stringify({
      key: listKey(window.location.href),
      first: Math.min(...pages),
      id: card.dataset.productId,
      top: card.getBoundingClientRect().top,
      at: Date.now(),
    }));
  } catch (_) {}
}

async function fetchPage(page) {
  const url = new URL(window.location.href);
  url.searchParams.set('page', String(page));
  url.searchParams.set('section_id', list.getAttribute('section-id'));
  url.hash = '';
  const response = await fetch(url, { credentials: 'same-origin' });
  if (!response.ok) return [];
  const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
  return [...doc.querySelectorAll('[ref="grid"] > [ref="cards[]"]')];
}

async function restore(persisted) {
  let saved = null;
  try {
    saved = JSON.parse(sessionStorage.getItem(KEY) || 'null');
  } catch (_) {}
  if (!saved || saved.key !== listKey(window.location.href) || Date.now() - saved.at > MAX_AGE) return;
  const nav = performance.getEntriesByType('navigation')[0];
  if (!persisted && nav?.type !== 'back_forward') return;
  try {
    sessionStorage.removeItem(KEY);
  } catch (_) {}

  const grid = list.querySelector('[ref="grid"]');
  if (!grid) return;
  if (!persisted) {
    const onScreen = cards().map((c) => Number(c.dataset.page)).filter(Number.isFinite);
    const wanted = [];
    for (let page = Math.min(...onScreen) - 1; page >= saved.first; page--) wanted.push(page);
    // Fetch together, insert nearest-first; <results-list> may prepend a page
    // of its own meanwhile, so anything already on screen is skipped.
    const fetched = await Promise.all(wanted.map((page) => fetchPage(page).catch(() => [])));
    for (const items of fetched) {
      const have = new Set(cards().map((c) => c.dataset.productId));
      grid.prepend(...items.filter((c) => !have.has(c.dataset.productId)));
    }
  }

  const card = list.querySelector(`[ref="cards[]"][data-product-id="${CSS.escape(String(saved.id))}"]`);
  if (!card) return;
  const container = getScrollContainer();
  const place = () => {
    const delta = card.getBoundingClientRect().top - saved.top;
    if (Math.abs(delta) > 1) container.scrollTo({ top: container.scrollTop + delta, behavior: 'instant' });
  };
  // Horizon's own restore and late-loading images both move things after us.
  place();
  requestAnimationFrame(place);
  setTimeout(place, 350);
  setTimeout(place, 900);
}

if (list) {
  document.addEventListener(
    'click',
    (event) => {
      const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
      const card = link?.closest('[ref="cards[]"]');
      if (card && list.contains(card)) save(card);
    },
    true
  );
  window.addEventListener('pageshow', (event) => restore(event.persisted));
}
